import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Plus, FileText, Edit2, Trash2, Loader2 } from 'lucide-react';
import InsuranceService from '../../services/InsuranceService';
import DocumentService from '../../services/DocumentService';
import { Card, Button, toast, ConfirmationModal } from './V2Components';

const formatCurrency = (value) => {
    if (value == null || value === '') return 'N/A';
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(value);
};

const formatDate = (dateString) => {
    if (dateString == null || dateString === '') return 'N/A';
    // Append local-time suffix to prevent UTC-midnight → previous-day shift west of UTC
    const date = new Date(dateString + 'T00:00:00');
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

const InsuranceTab = ({ houseId }) => {
    const navigate = useNavigate();
    const [policies, setPolicies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deleteCandidate, setDeleteCandidate] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [downloadingId, setDownloadingId] = useState(null);

    const fetchPolicies = useCallback(async () => {
        try {
            setLoading(true);
            const data = await InsuranceService.getHousePolicies(houseId);
            setPolicies(data);
        } catch (error) {
            toast.error?.('Failed to load insurance policies');
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, [houseId]);

    useEffect(() => {
        if (houseId) {
            fetchPolicies();
        }
    }, [houseId, fetchPolicies]);

    const handleViewProof = async (documentId) => {
        // The /documents/:id/download endpoint returns {url, filename, expires_in},
        // not the file itself — we must resolve the presigned URL first, then open it.
        if (!documentId) return;
        setDownloadingId(documentId);
        try {
            const resp = await DocumentService.getDownloadUrl(documentId);
            const url = resp?.url ?? resp?.data?.url;
            if (!url) {
                toast.error?.('Could not generate document download link');
                return;
            }
            window.open(url, '_blank', 'noopener,noreferrer');
        } catch (error) {
            toast.error?.('Failed to open document');
            console.error(error);
        } finally {
            setDownloadingId(null);
        }
    };

    const handleConfirmDelete = async () => {
        if (!deleteCandidate) return;
        setDeleting(true);
        try {
            await InsuranceService.deletePolicy(deleteCandidate.id);
            toast.success?.('Policy deleted');
            fetchPolicies();
        } catch (error) {
            toast.error?.('Failed to delete policy');
        } finally {
            setDeleting(false);
            setDeleteCandidate(null);
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center mb-4">
                <div>
                    <h3 className="text-lg font-bold text-[var(--text-main)]">Insurance Policies</h3>
                    <p className="text-xs text-[var(--text-secondary)] mt-1">
                        Track your home insurance policies and coverage limits.
                    </p>
                </div>
                <Button leadingIcon={Plus} onClick={() => navigate(`/houses/${houseId}/insurance/new`)}>
                    Add Policy
                </Button>
            </div>

            {loading ? (
                <div className="flex justify-center p-12">
                    <Loader2 className="animate-spin text-[var(--primary)]" size={32} />
                </div>
            ) : policies.length === 0 ? (
                <Card className="p-10 text-center">
                    <Shield size={48} className="mx-auto mb-3 text-[var(--text-muted)] opacity-50" />
                    <p className="text-[var(--text-muted)] mb-4">No insurance policies found.</p>
                    <Button variant="outline" onClick={() => navigate(`/houses/${houseId}/insurance/new`)}>Add your first policy</Button>
                </Card>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {policies.map(policy => (
                        <Card key={policy.id} className="p-5 flex flex-col justify-between">
                            <div>
                                <div className="flex justify-between items-start mb-4">
                                    <div>
                                        <h4 className="font-bold text-lg text-[var(--text-main)]">{policy.insurer}</h4>
                                        <p className="text-sm text-[var(--text-secondary)] font-mono">{policy.policy_number}</p>
                                    </div>
                                    <div className="flex space-x-1">
                                        <button
                                            onClick={() => navigate(`/houses/${houseId}/insurance/${policy.id}/edit`)}
                                            className="p-1.5 text-[var(--text-secondary)] hover:text-[var(--primary)] bg-[var(--bg-hover)] rounded"
                                            aria-label={`Edit ${policy.insurer} policy`}
                                            title="Edit policy"
                                        >
                                            <Edit2 size={16} />
                                        </button>
                                        <button
                                            onClick={() => setDeleteCandidate(policy)}
                                            className="p-1.5 text-[var(--text-secondary)] hover:text-red-500 bg-[var(--bg-hover)] rounded"
                                            aria-label={`Delete ${policy.insurer} policy`}
                                            title="Delete policy"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-y-2 mb-4 text-sm">
                                    <div>
                                        <span className="text-[var(--text-muted)] text-xs uppercase block">Coverage Period</span>
                                        <span className="font-medium text-[var(--text-main)]">{formatDate(policy.start_date)} - {formatDate(policy.end_date)}</span>
                                    </div>
                                    <div>
                                        <span className="text-[var(--text-muted)] text-xs uppercase block">Annual Premium</span>
                                        <span className="font-medium text-[var(--text-main)]">{formatCurrency(policy.total_amount)}</span>
                                    </div>
                                    <div>
                                        <span className="text-[var(--text-muted)] text-xs uppercase block">Deductible</span>
                                        <span className="font-medium text-[var(--text-main)]">{formatCurrency(policy.deductible)}</span>
                                    </div>
                                    <div>
                                        <span className="text-[var(--text-muted)] text-xs uppercase block">Wind/Hail Deductible</span>
                                        <span className="font-medium text-[var(--text-main)]">{formatCurrency(policy.wind_hail_deductible)}</span>
                                    </div>
                                </div>

                                {policy.document && (
                                    <div className="mt-4 pt-4 border-t border-[var(--border)]">
                                        <button
                                            type="button"
                                            onClick={() => handleViewProof(policy.document.id)}
                                            disabled={downloadingId === policy.document.id}
                                            className="flex items-center text-sm text-[var(--primary)] hover:underline disabled:opacity-60"
                                            aria-label={`View proof of insurance for ${policy.insurer}`}
                                        >
                                            {downloadingId === policy.document.id ? (
                                                <Loader2 size={16} className="mr-2 animate-spin" />
                                            ) : (
                                                <FileText size={16} className="mr-2" />
                                            )}
                                            View Proof of Insurance
                                        </button>
                                    </div>
                                )}
                            </div>
                        </Card>
                    ))}
                </div>
            )}

            <ConfirmationModal
                isOpen={!!deleteCandidate}
                onClose={() => setDeleteCandidate(null)}
                onConfirm={handleConfirmDelete}
                isLoading={deleting}
                title={`Delete ${deleteCandidate?.insurer ?? 'policy'}?`}
                message="This will permanently remove the insurance policy. This action cannot be undone."
                confirmText={deleting ? 'Deleting...' : 'Delete'}
                isDanger={true}
                icon={Trash2}
            />
        </div>
    );
};

export default InsuranceTab;
